/**
 * تورِ راهنمای کاربرِ تازه — چهار قدمِ کوتاه، روی خودِ صفحه (WHOSUP-83).
 *
 * ═══ چه چیزی را حل می‌کند ═══
 *
 * تا امروز کاربرِ تازه بعد از ساختِ نمایه وسطِ تبِ کشف رها می‌شد: یک فهرستِ
 * رخداد، چهار تب، و هیچ جمله‌ای درباره‌ی اینکه کارِ اصلیِ این اپ چیست. کسی
 * که نمی‌داند «سیگنال» یعنی چه، خودش کشفش نمی‌کند.
 *
 * ═══ چهار تصمیمی که شکلش را تعیین کرده (قرارِ ۱۵ اوت) ═══
 *
 * ۱. **بعد از ساختِ نمایه**، نه قبل از ورود. اسلایدشوی پیش از ثبت‌نام عمداً
 *    رد شد: کسی که هنوز حساب ندارد دلیلی ندارد چهار صفحه معرفی بخواند.
 * ۲. **روی خودِ صفحه**، نه یک صفحه‌ی جدا. کارت کوچک است و پشتش همان اپِ
 *    واقعی دیده می‌شود؛ هر قدم به تبی که درباره‌اش حرف می‌زند اشاره می‌کند
 *    (`tab` پایین) — روی گوشی با یک فلش بالای همان تب، روی وب با برجسته
 *    شدنِ همان لینکِ هدر.
 * ۳. **قابلِ رد کردن از قدمِ اول.** «رد کن» از همان قدمِ یک هست و تور دیگر
 *    برنمی‌گردد.
 * ۴. **هر دو اپ.** پس چیدنِ قدم‌ها، قدمِ بعدی و «دیده شد» همه این‌جاست و
 *    اپ‌ها فقط رندرش می‌کنند.
 *
 * ═══ «دیده شد» روی دستگاه می‌نشیند، نه روی نمایه ═══
 *
 * همان انتخابِ `noFriendsNotice`: نه ستونِ تازه‌ای در دیتابیس می‌خواهد، نه
 * مسیرِ خطای تازه‌ای. بدترین حالتش این است که همان آدم روی گوشیِ دومش یک‌بار
 * دیگر تور را ببیند — که برای یک راهنمای چهار قدمی اشکالی ندارد.
 *
 * ═══ چرا «pending» و نه «هر کسی که پرچم ندارد» ═══
 *
 * پرچمِ نبودن یعنی «این دستگاه تا حالا تور را نشان نداده» — که برای هر
 * کاربرِ قدیمی که مرورگرش را عوض کرده هم درست است. اگر مبنا آن بود، هر ورود
 * از یک دستگاهِ تازه یک تورِ ناخواسته می‌شد. پس علامت را **ساختِ نمایه**
 * می‌گذارد (`markTourPending`) و تور فقط همان را می‌بیند.
 */
import { useEffect, useReducer } from 'react';
import { storage } from '../platform.ts';
import { recordPref } from './prefSink.ts';

/** تبی که این قدم درباره‌اش حرف می‌زند — اپ‌ها ازش برای اشاره استفاده می‌کنن. */
export type TourTab = 'signals' | 'discover' | 'friends' | 'settings';

export interface TourStep {
  id: 'discover' | 'create' | 'invite' | 'notifications';
  tab: TourTab;
  /** کلیدهای i18n — متن در `lib/i18nLocales/*` است، نه این‌جا. */
  titleKey: string;
  bodyKey: string;
}

/**
 * چهار قدم، به ترتیبی که کاربرِ تازه واقعاً طی می‌کند: این‌جا چه خبر است →
 * کارِ اصلی چیست → چطور به دوستانت می‌رسد → جوابشان چطور به تو می‌رسد.
 *
 * ⚠ بیشتر از چهار تا عمداً نه: راهنمایی که طولانی شود همان چیزی می‌شود که
 * کاربر ردش می‌کند، و آن‌وقت هیچ‌کدام از چهار تا خوانده نمی‌شود.
 */
export const TOUR_STEPS: readonly TourStep[] = [
  { id: 'discover', tab: 'discover', titleKey: 'tourDiscoverTitle', bodyKey: 'tourDiscoverBody' },
  { id: 'create', tab: 'signals', titleKey: 'tourCreateTitle', bodyKey: 'tourCreateBody' },
  /**
   * قدمِ سوم دو چیز را با هم می‌گوید، چون جدا کردنشان قدمِ پنجم می‌خواست:
   * (الف) بدونِ دوست هم می‌شود سیگنال ساخت، (ب) سیگنال از چهار راه به آدم‌ها
   * می‌رسد — اعلانِ داخلِ اپ، ایمیلِ دعوت، پیامکِ از قبل نوشته‌شده، و لینکی
   * که هر جایی می‌شود فرستاد. این دومی خواسته‌ی صریحِ تیکت است: کاربر باید
   * بداند «دوستم اپ را ندارد» بن‌بست نیست.
   */
  { id: 'invite', tab: 'friends', titleKey: 'tourInviteTitle', bodyKey: 'tourInviteBody' },
  { id: 'notifications', tab: 'settings', titleKey: 'tourNotifTitle', bodyKey: 'tourNotifBody' },
];

/** یه کلید به‌ازای هر کاربر: یه دستگاه ممکنه چند حساب ببینه. */
const keyFor = (userId: string): string => `whosup.tour.v1.${userId}`;

/** «نمایه همین الان ساخته شد» — تنها چیزی که تور را روشن می‌کند. */
export async function markTourPending(userId: string): Promise<void> {
  if (!userId) return;
  try {
    await storage().setItem(keyFor(userId), 'pending');
  } catch {
    // ذخیره نشد؛ بدترین حالت اینه که کاربرِ تازه تور رو نبینه.
  }
}

/** تور تمام شد یا رد شد — هر دو یعنی «دیگه خودش نیاد». */
export async function markTourSeen(userId: string): Promise<void> {
  if (!userId) return;
  try {
    await storage().setItem(keyFor(userId), 'seen');
  } catch {
    // ذخیره نشد؛ بدترین حالت اینه که دفعه‌ی بعد یه‌بار دیگه بیاد.
  }
}

/** آیا این کاربر همین حالا نمایه ساخته و هنوز تور را ندیده؟ */
export async function isTourPending(userId: string): Promise<boolean> {
  if (!userId) return false;
  try {
    return (await storage().getItem(keyFor(userId))) === 'pending';
  } catch {
    return false;
  }
}

/**
 * قدمِ بعدی، یا `null` اگر تور تمام شده.
 *
 * تابعِ خالص و جدا نگه داشته شده تا «آخرین قدم» یک‌جا تعریف شود؛ وگرنه هر
 * دو اپ باید `index + 1 < TOUR_STEPS.length` را خودشان درست می‌نوشتند.
 */
export function nextTourIndex(index: number): number | null {
  const next = index + 1;
  return next < TOUR_STEPS.length ? next : null;
}

/** Mobile offers its visual tour on the first signed-in visit to this install.
 * Reuse the account's existing flag, so the old introduction also counts as seen.
 */
export async function hasSeenTour(userId: string): Promise<boolean> {
  if (!userId) return true;
  try {
    return (await storage().getItem(keyFor(userId))) === 'seen';
  } catch {
    return true; // A storage error must not repeatedly interrupt sign-in.
  }
}

/* ── حالتِ باز/بسته ─────────────────────────────────────────────
 *
 * یک حالتِ ماژولی با شنونده، نه یک Context: تور را هم صفحه‌ی تنظیمات باز
 * می‌کند («دوباره ببین») و هم خودِ بوتِ کاربرِ تازه، و هم ناوبریِ اپ باید
 * بداند الان کدام تب برجسته است. یک Context یعنی همه‌ی این‌ها زیرِ یک
 * Provider، که روی موبایل (expo-router) و وب (react-router) دو جای متفاوت
 * می‌افتاد. همان الگوی `i18n.ts`.
 */

let openFor: { userId: string; index: number } | null = null;
const listeners = new Set<() => void>();
const emit = (): void => listeners.forEach((fn) => fn());

export interface OpenTour {
  step: TourStep;
  /** از صفر. برای «۲ از ۴» یکی اضافه کن. */
  index: number;
  total: number;
}

const snapshot = (): OpenTour | null => (openFor
  ? { step: TOUR_STEPS[openFor.index], index: openFor.index, total: TOUR_STEPS.length }
  : null);

/** تور را از قدمِ اول باز می‌کند — ردیفِ «دوباره ببین» در تنظیمات. */
export function startTour(userId: string): void {
  if (!userId) return;
  openFor = { userId, index: 0 };
  emit();
}

/**
 * فقط اگر نمایه همین الان ساخته شده باشد. هر دو اپ سرِ سوار شدنِ قابِ اصلی
 * صداش می‌زنن؛ برای کاربرِ قدیمی هیچ کاری نمی‌کنه.
 */
export async function openTourIfPending(userId: string): Promise<void> {
  if (!userId || openFor) return;
  if (await isTourPending(userId)) startTour(userId);
}

/** قدمِ بعد، یا بستنِ تور اگر این آخرینش بود. */
export function advanceTour(): void {
  if (!openFor) return;
  const next = nextTourIndex(openFor.index);
  if (next === null) { endTour('done'); return; }
  openFor = { ...openFor, index: next };
  emit();
}

/**
 * بستنِ تور. `skipped` یعنی وسطِ راه رد شد، `done` یعنی تا آخر رفت — هر دو
 * «دیده شد»اند و فرقشان فقط یک عدد در گزارش است.
 *
 * ⚠ نوشتنِ پرچم `await` نمی‌شود: کارت باید همان لمس بسته شود، نه بعد از یک
 * رفت‌وبرگشتِ حافظه‌ی دستگاه.
 */
export function endTour(reason: 'done' | 'skipped'): void {
  const open = openFor;
  if (!open) return;
  openFor = null;
  emit();
  recordPref('tour', reason);
  void markTourSeen(open.userId);
}

/** فقط برای تست — حالتِ ماژولی بینِ تست‌ها باقی می‌ماند. */
export function resetTourState(): void {
  openFor = null;
  listeners.clear();
}

/**
 * تورِ باز (یا `null`). هم کارتِ راهنما ازش می‌خونه، هم ناوبری — که بدونه
 * کدوم تب رو برجسته کنه.
 */
export function useTour(): OpenTour | null {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    listeners.add(force);
    return () => { listeners.delete(force); };
  }, []);
  return snapshot();
}
